import InputFieldwithSearch from "../inputfields/InputFieldwithSearch";
import ButtonWithImg from "../buttons/ButtonWithImg";
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { showToast } from "../toastMessage/toastSlice";
import useApi from "../../services/useApi";
import useTranslation from "../CustomHooks/useTranslation";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import DynamicFilterPanel, { DynamicFilterConfig } from "../DynamicComponent/DynamicFilterPanel";
import CheckBoxWithLabel from "../checkbox/CheckBoxWithLabel";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomModal from "../CustomModal";


interface AccountSummaryData {
  opening_balance?: string;
  invoice_amount?: string;
  amount_paid?: string;
  balance_due?: string;
  total_dr?: string;
  total_cr?: string;
  closing_balance?: string;
}

interface GridTableTopBarProps {
  onSearch: (value: string) => void;
  onFilter: (filters: { [key: string]: string | boolean | null } | null) => void;
  searchValue: string | null;
  loading: boolean;
  searchPlaceholder: string | undefined;
  searchParams: URLSearchParams;
  filters: boolean;
  serviceNow?: boolean;
  exporting: boolean;
  endpoint?: string;
  disableExport?: boolean;
  disableSearchAndFilter?: boolean;
  placeholderClassName?: string;
  mainEndPoint?: string;
  dynamicFilterConfig?: DynamicFilterConfig;
  recordCount?: number;
  noSearch?: boolean;
  customHeight?: string;
  filterShimmer?: boolean;
  balanceSheet?: boolean;
  reportType?: string;
  ticketList?: boolean;
  accountSummary?: AccountSummaryData;
  enableCheckboxes?: boolean;
  onSelectAll?: () => void;
  onSelectAllPages?: () => void;
  currentPageSelected?: boolean;
  allPagesSelected?: boolean;
  selectedCount?: number;
  totalCount?: number;
  currentPageData?: unknown[];
  selectedIds?: string[];
  removeBorder?: boolean;
  middleContent?: React.ReactNode;
  listTitle?: string;
  topRightContent?: React.ReactNode;
}

const GridTableTopBar = ({
  onSearch,
  onFilter,
  searchValue,
  searchParams,
  searchPlaceholder,
  loading,
  filters,
  endpoint,
  disableExport,
  exporting,
  disableSearchAndFilter,
  placeholderClassName,
  dynamicFilterConfig,
  recordCount,
  noSearch = false,
  customHeight,
  filterShimmer = false,
  balanceSheet = false,
  reportType,
  ticketList = false,
  accountSummary,
  enableCheckboxes = false,
  onSelectAll,
  onSelectAllPages,
  currentPageSelected = false,
  allPagesSelected = false,
  selectedCount = 0,
  totalCount = 0,
  currentPageData = [],
  selectedIds = [],
  removeBorder = false,
  middleContent,
  listTitle,
  topRightContent
}: GridTableTopBarProps) => {
  const [isExportOpen, setIsExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const [activateDeactivateModal, setActivateDeactivateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { translate, notEnglish } = useTranslation(breadcrumbsContent)
  const { getAPI, PatchAPI } = useApi();
  const [exportLoading, setExportLoading] = useState(false);
  const handleClickOutside = (event: MouseEvent) => {
    if (
      exportRef.current &&
      !exportRef.current.contains(event.target as Node)
    ) {
      setIsExportOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const exportOptions = [
    { label: "CSV", value: "csv", textColorClass: "text-positive" },
    { label: "PDF", value: "pdf", textColorClass: "text-s_dark_hover" },
    ...(reportType ? [{ label: "EXCEL", value: "xlsx", textColorClass: "text-positive" }] : []),
  ];

  const shouldHideFilterAndExport = () => {
    if (Number(recordCount) !== 0) return false;

    const searchParamKeys = Array.from(searchParams.keys());

    const hasOnlyAllowedKeys = searchParamKeys.every(key =>
      key === 'page_number' || key === 'sort'
    );

    return hasOnlyAllowedKeys;
  };

  const hideFilterAndExport = shouldHideFilterAndExport();

  // Account Summary Component
  const AccountSummary = () => {
    if (reportType !== "Account Statement Report") return null;
    if (!accountSummary) return null;
    // Show shimmer when loading
    if (loading) {
      return (
        <div className="bg-white border-2 w-[424px] flex flex-col border-gray-200 rounded-2xl items-center p-4 mb-1">
          <div className="w-full">
            <div className="border-b-2 border-gray-200 w-full pb-2 mb-2">
              <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>

            <div className="w-full space-y-3">
              <div className="flex justify-between w-full items-center">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="h-5 bg-gray-200 rounded w-20 animate-pulse"></div>
              </div>
              <div className="flex justify-between w-full items-center">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="h-5 bg-gray-200 rounded w-20 animate-pulse"></div>
              </div>
              <div className="flex justify-between w-full items-center">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="h-5 bg-gray-200 rounded w-20 animate-pulse"></div>
              </div>
              <div className="flex justify-between border-t-2 mt-2 pt-2 border-gray-200 w-full items-center">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                <div className="h-5 bg-gray-200 rounded w-20 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Show actual data when not loading


    return (
      <div className="bg-white border-2 w-[424px] flex flex-col border-gray-200 rounded-2xl items-center p-4 mb-1">
        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b-2 border-gray-200 w-full text-center pb-2">Account Summary</h3>

        <div className="w-full">
          <div className="text-center flex justify-between w-full items-center">
            <p className="text-sm text-gray-600 mb-1">Opening Balance</p>
            <p className="text-lg font-semibold text-black">
              AFA {accountSummary.opening_balance || "0.00"}
            </p>
          </div>
          <div className="text-center flex justify-between w-full items-center">
            <p className="text-sm text-gray-600 mb-1">Invoice Amount</p>
            <p className="text-lg font-semibold text-black">
              AFA {accountSummary.invoice_amount || "0.00"}
            </p>
          </div>
          <div className="text-center flex justify-between w-full items-center">
            <p className="text-sm text-gray-600 mb-1">Amount Paid</p>
            <p className="text-lg font-semibold text-black">
              AFA {accountSummary.amount_paid || "0.00"}
            </p>
          </div>
          <div className="text-center flex justify-between border-t-2 mt-2 pt-2 border-gray-200 w-full items-center">
            <p className="text-sm text-gray-600 mb-1">Balance Due</p>
            <p className="text-lg font-semibold text-black">
              AFA {accountSummary.closing_balance || "0.00"}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Checkbox Selection Component
  const CheckboxSelection = () => {
    if (!enableCheckboxes) return null;

    const currentPageCount = currentPageData?.length || 0;
    const isCurrentPageFullySelected = currentPageSelected && currentPageCount > 0;
    const isAnySelection = (allPagesSelected || currentPageSelected || selectedCount >= currentPageData?.length);

    return (
      <div className={`flex flex-col gap-2 mt-2 mx-4`}>
        {/* Select All Checkbox */}
        {totalCount > 0 && <CheckBoxWithLabel
          label="Select All"
          checked={isCurrentPageFullySelected || allPagesSelected}
          handelCheckBox={() => onSelectAll?.()}
          id="select-all-checkbox"
          textClassName="text-sm font-medium text-gray-700"
        />}

        {/* Selection Info Banner */}
        {isAnySelection && totalCount > 10 && (
          <div className="bg-p_light p-3">
            <div className="flex items-center justify-center gap-4">
              <div className="text-sm text-black">
                {allPagesSelected ? (
                  <span>All <strong>{totalCount}</strong> users in the list are selected.</span>
                ) : currentPageSelected ? (
                  <span>All <strong>{currentPageCount}</strong> users on this page are selected.</span>
                ) : (
                  <span><strong>{selectedCount}</strong> users are selected.</span>
                )}
              </div>
              {currentPageSelected && !allPagesSelected && (
                <button
                  onClick={onSelectAllPages}
                  className="text-p_primary hover:text-p_dark_hover text-sm font-medium underline"
                >
                  Select all <strong>{totalCount}</strong> users in the list
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleExportOptionClick = async (value: string): Promise<void> => {
    setIsExportOpen(false);
    try {
      setExportLoading(true);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await getAPI(`${endpoint}&export_format=${value}&client_timezone=${timezone}`);
      if (!res.error) {
        if (res.data.url) {
          window.open(res.data.url, "_blank");
          dispatch(
            showToast({
              type: "success",
              message: translate("List exported successfully"),
            })
          );
        } else {
          dispatch(
            showToast({
              type: "success",
              message: res.data.message || translate(`Export in progress. You'll receive an email with the ${value.toLocaleUpperCase()} once it's ready.`),
            })
          );
        }
      } else {
        dispatch(
          showToast({
            type: "error",
            message: "something went wrong",
          })
        );
      }
    } catch {
      dispatch(
        showToast({
          type: "error",
          message: "something went wrong",
        })
      );
    } finally {
      setExportLoading(false);
    }
  };

  const handleModalSubmit = async (text?: string) => {
    const isActivating = location.pathname.endsWith("/activate-childs");
    
    // Validate deactivate reason if deactivating
    if (!isActivating && (!text || !text.trim())) {
      dispatch(
        showToast({
          type: "error",
          message: translate("Please provide a reason for deactivation"),
        })
      );
      return;
    }

    // Determine child_ids based on selection
    let childIds: string[] | string;
    if (allPagesSelected) {
      // If all pages are selected, send ["All"]
      childIds = "all";
    } else if (selectedIds.length > 0) {
      // If specific users are selected, send their IDs
      childIds = selectedIds;
    } else {
      dispatch(
        showToast({
          type: "error",
          message: translate("Please select users to update"),
        })
      );
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Get user_role from search params
      const userRole = searchParams.get("user_role");
      
      const response = await PatchAPI(`merchants/status/${id}`, {
        status: isActivating,
        child_ids: childIds as string[],
        ...(userRole && childIds === "all" && { user_role: userRole }),
        ...(text && { deactivate_reason: text })
      });
      
      if (!response?.error) {
        dispatch(
          showToast({
            type: "success",
            message: translate(isActivating ? "Users activated successfully" : "Users deactivated successfully"),
          })
        );
        navigate(location.pathname.split("/").slice(0, -1).join("/"));
        // Close modal and reset state
        setActivateDeactivateModal(false);
      } else {
        dispatch(
          showToast({
            type: "error",
            message: translate(response.data.response.data.message),
          })
        );
        setActivateDeactivateModal(false);
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      dispatch(
        showToast({
          type: "error",
          message: translate("Failed to update user status") || "Something went wrong",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div dir={notEnglish ? "rtl" : "ltr"}>
      <div
        className={`flex justify-between items-start py-4 ${removeBorder ? 'border-none' : ''} pr-6 pl-4 ${disableSearchAndFilter || hideFilterAndExport ? "" : "border-b"
          }`}
      >
        <AccountSummary />
        <div className="flex flex-col">
          {listTitle && (
            <h2 className="text-[24px] font-semibold text-primary_text mb-2">{listTitle}</h2>
          )}
          <div className="w-[310px]">
            {!noSearch && (!disableSearchAndFilter || loading) && (
              <InputFieldwithSearch
                handleSearchValue={onSearch}
                testId="search_id_table"
                searchValue={searchValue}
                placeHolder={searchPlaceholder}
                placeholderClassName={placeholderClassName}
              />
            )}
          </div>
        </div>
        {middleContent && !topRightContent && (
          <div className="flex-1 flex items-center justify-center">
            {middleContent}
          </div>
        )}
        <div className="flex flex-col items-end">
          {topRightContent && (
            <div className="mb-2">
              {topRightContent}
            </div>
          )}
          <div
            className={`flex items-center justify-center ${!ticketList ? "gap-x-3" : ""
              }`}
          >
          {!hideFilterAndExport && reportType !== "Account Statement Report" && filters && ((!disableSearchAndFilter) || loading) && dynamicFilterConfig && (
              <DynamicFilterPanel
                config={dynamicFilterConfig}
                onApply={onFilter}
                onClear={() => onFilter(null)}
                initialValues={{}}
                className=""
                customHeight={customHeight}
                searchParams={searchParams}
                balanceSheet={balanceSheet}
                ticketList={ticketList}
            />
          )}
          {location.pathname.endsWith("childs") && totalCount > 0 && (
            <ButtonWithImg
              buttonName={location.pathname.endsWith("/activate-childs") ? translate("Activate") : translate("Deactivate")}
              disabledButton={loading || (selectedCount === 0 && !allPagesSelected)}
              testId="activate"
              className={`px-4 ${location.pathname.endsWith("/activate-childs") ? "bg-positive border-none" : "bg-negative border-none"} text-white ${notEnglish ? "w-fit" : "w-[110px]"}`}
              buttonOnclick={() => setActivateDeactivateModal(true)}
            />
          )}
          {!hideFilterAndExport && !ticketList && exporting && reportType !== "Account Statement Report" && (
            <div className="relative" ref={exportRef}>
              {((exporting && !disableSearchAndFilter) || loading) && (
                <ButtonWithImg
                  buttonName={translate("Export")}
                  disabledButton={loading || disableExport}
                  ImgSrc={loading || disableExport ? "loading-export" : "export"}
                  testId="export"
                  className={`px-4 bg-p_primary text-white ${notEnglish ? "w-fit" : "w-[110px]"
                    } `}
                  buttonOnclick={() => setIsExportOpen(!isExportOpen)}
                  loading={exportLoading}
                />
              )}
              {isExportOpen && (
                <div className="absolute right-0 mt-1 w-[110px] bg-white border border-s_dark_hover rounded-lg z-[70]">
                  <ul className="flex flex-col">
                    {exportOptions.map((option) => (
                      <li
                        key={option.value}
                        data-testid={`${option.value}`}
                        className={`p-2 text-body2 cursor-pointer ${option.textColorClass} hover:bg-p_light rounded-lg`}
                        onClick={() => handleExportOptionClick(option.value)}
                      >
                        {option.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
      <CheckboxSelection />
      {activateDeactivateModal && (
            <CustomModal
              isOpen={activateDeactivateModal}
              onClose={() => {
                setActivateDeactivateModal(false);
              }}
              title={location.pathname.endsWith("/activate-childs") ? translate("Confirm Activation") : translate("Confirm Deactivation")}
              subText={location.pathname.endsWith("/activate-childs") ? translate("Are you sure you want to activate the selected users?") : translate("To deactivate these users, please enter the reason for deactivation")}
              primaryButtonText={location.pathname.endsWith("/activate-childs") ? translate("Activate") : translate("Deactivate")}
              handelSubmit={handleModalSubmit}
              buttonColor={location.pathname.endsWith("/activate-childs") ? "bg-positive" : "bg-negative"}
              textArea={location.pathname.endsWith("/activate-childs") ? false : true}
              buttonLoading={isSubmitting}
              buttonWidth={notEnglish ? "w-fit" : "w-[110px]"}
              secondaryButton={location.pathname.endsWith("/activate-childs") ? true : false}
              secondaryButtonText={translate("Cancel")}
          />
      )}
    </div>
  );
};

export default GridTableTopBar;
